import {defineMessages} from 'react-intl';

/*
 * Maps authentication failures to user-facing explanations.
 *
 * This lives in its own module, separate from the dialog, for two reasons:
 *
 *   1. It is pure, so it can be unit tested without rendering React or a Redux
 *      store.
 *   2. A missing entry used to fall through to a single generic message, which
 *      hid the real cause: a wrong password looked identical to a broken
 *      server. The test suite asserts that every code the service can return
 *      has its own explanation, so that cannot regress silently.
 */
const messages = defineMessages({
    accountExists: {
        id: 'gui.login.accountExists',
        defaultMessage: '该账号已被注册，请直接登录或更换账号。',
        description: 'Registration error for a duplicate account'
    },
    empty: {
        id: 'gui.login.empty',
        defaultMessage: '请输入账号和密码。',
        description: 'Error shown when account or password is empty'
    },
    invalidAccount: {
        id: 'gui.login.invalidAccount',
        defaultMessage: '账号需为 3–32 位字母、数字、下划线、连字符或点号。',
        description: 'Error shown when the account does not match the allowed pattern'
    },
    invalidCredentials: {
        id: 'gui.login.invalidCredentials',
        defaultMessage: '账号或密码错误。若尚未注册，请点下方「没有账号？立即注册」。',
        description: 'Error shown when the account does not exist or the password is wrong'
    },
    invalidDisplayName: {
        id: 'gui.login.invalidDisplayName',
        defaultMessage: '显示名称不能为空，且不能超过 64 个字符。',
        description: 'Error shown when the display name is invalid'
    },
    invalidPassword: {
        id: 'gui.login.invalidPassword',
        defaultMessage: '密码长度需为 10–128 位，当前不符合要求。',
        description: 'Error shown when the password fails the length rules'
    },
    invalidSession: {
        id: 'gui.login.invalidSession',
        defaultMessage: '登录状态已失效，请重新登录。',
        description: 'Error shown when the stored session is no longer valid'
    },
    failed: {
        id: 'gui.login.failed',
        defaultMessage: '操作失败，请检查输入内容或网络后重试。',
        description: 'Generic authentication failure message'
    },
    malformedRequest: {
        id: 'gui.login.malformedRequest',
        defaultMessage: '提交的数据格式异常，请重试；若持续出现请查看后端终端输出。',
        description: 'Shown when the service rejects the request body'
    },
    network: {
        id: 'gui.login.network',
        defaultMessage: '无法连接认证服务，请确认后端已启动。',
        description: 'Authentication server unreachable message'
    },
    passwordHint: {
        id: 'gui.login.passwordHint',
        defaultMessage: '至少 {min} 位字符。',
        description: 'Hint shown under the password field during registration'
    },
    rateLimited: {
        id: 'gui.login.rateLimited',
        defaultMessage: '尝试过于频繁，请稍后再试。',
        description: 'Login request rate limited message'
    },
    accountDisabled: {
        id: 'gui.login.accountDisabled',
        defaultMessage: 'This account has been disabled. Please contact an administrator.',
        description: 'Shown when a disabled account tries to sign in'
    },
    notAdmin: {
        id: 'gui.admin.notAdmin',
        defaultMessage: 'Only administrators can use this feature.',
        description: 'Shown when an ordinary account opens an admin-only screen'
    },
    cannotModifySelf: {
        id: 'gui.admin.cannotModifySelf',
        defaultMessage: 'You cannot change your own role or disable your own account.',
        description: 'Shown when an administrator targets their own account'
    },
    cannotDeleteSelf: {
        id: 'gui.admin.cannotDeleteSelf',
        defaultMessage: 'You cannot delete your own account.',
        description: 'Shown when an administrator tries to delete themselves'
    },
    userNotFound: {
        id: 'gui.admin.userNotFound',
        defaultMessage: 'That account no longer exists. Refresh the list.',
        description: 'Shown when the targeted account has already been removed'
    },
    projectNotFound: {
        id: 'gui.admin.projectNotFound',
        defaultMessage: 'That project no longer exists. Refresh the list.',
        description: 'Shown when the targeted project has already been removed'
    },
    invalidRole: {
        id: 'gui.admin.invalidRole',
        defaultMessage: 'The role must be either administrator or user.',
        description: 'Shown when an unknown role is submitted'
    },
    invalidDisabled: {
        id: 'gui.admin.invalidDisabled',
        defaultMessage: 'The disabled state must be either on or off.',
        description: 'Shown when an invalid disabled state is submitted'
    },
    projectOpenFailed: {
        id: 'gui.myProjects.openFailed',
        defaultMessage: '打开作品失败，内容可能已损坏。可以重新保存一次当前作品。',
        description: 'Shown when a stored project could not be loaded into the editor'
    },
    projectUnreadable: {
        id: 'gui.myProjects.unreadable',
        defaultMessage: '这个作品是在旧版本里保存的，内容已经损坏，无法打开。请重新保存一次。',
        description: 'Shown when a stored project cannot be decoded into an archive'
    },
    requestTooLarge: {
        id: 'gui.login.requestTooLarge',
        defaultMessage: '提交内容过大，请缩短后重试。',
        description: 'Shown when the request body exceeds the service limit'
    },
    registerEmptyDisplayName: {
        id: 'gui.login.registerEmptyDisplayName',
        defaultMessage: '注册时请输入显示名称。',
        description: 'Error shown when the display name is missing during registration'
    },
    serverError: {
        id: 'gui.login.serverError',
        defaultMessage: '认证服务返回了错误（HTTP {status}），请查看后端终端输出。',
        description: 'Shown when the authentication service returns an unexpected error status'
    },
    serviceMisconfigured: {
        id: 'gui.login.serviceMisconfigured',
        defaultMessage: '认证服务的来源或接口配置不匹配，请确认 GUI_ORIGIN 配置与所用版本。',
        description: 'Shown when the service rejects the origin or the route is missing'
    }
});

// Error codes answered by auth-server/src/app.js, mapped to their explanation.
const CODE_MESSAGES = {
    ACCOUNT_EXISTS: messages.accountExists,
    INVALID_ACCOUNT: messages.invalidAccount,
    INVALID_CREDENTIALS: messages.invalidCredentials,
    INVALID_DISPLAY_NAME: messages.invalidDisplayName,
    INVALID_PASSWORD: messages.invalidPassword,
    INVALID_SESSION: messages.invalidSession,
    UNAUTHENTICATED: messages.invalidSession,
    NETWORK_ERROR: messages.network,
    TOO_MANY_REQUESTS: messages.rateLimited,
    INVALID_JSON: messages.malformedRequest,
    PAYLOAD_TOO_LARGE: messages.requestTooLarge,
    CORS_ORIGIN_NOT_ALLOWED: messages.serviceMisconfigured,
    NOT_FOUND: messages.serviceMisconfigured,
    // A project stored by the older text-based save path lost its binary bytes
    // and can never be reopened, so say that plainly instead of guessing.
    PROJECT_UNREADABLE: messages.projectUnreadable,
    PROJECT_OPEN_FAILED: messages.projectOpenFailed,
    // Administrator actions. These are shown inside the administration screen,
    // where a bare server sentence would arrive in the wrong language.
    ACCOUNT_DISABLED: messages.accountDisabled,
    NOT_ADMIN: messages.notAdmin,
    CANNOT_MODIFY_SELF: messages.cannotModifySelf,
    CANNOT_DELETE_SELF: messages.cannotDeleteSelf,
    USER_NOT_FOUND: messages.userNotFound,
    PROJECT_NOT_FOUND: messages.projectNotFound,
    INVALID_ROLE: messages.invalidRole,
    INVALID_DISABLED: messages.invalidDisabled
};

/*
 * Returns the message descriptor for a failure. `error` is whatever the auth
 * API client rejected with: it carries `code`, and for server replies also
 * `serverMessage` and `status`.
 */
const describeAuthError = error => {
    const code = error && error.code;

    if (code && CODE_MESSAGES[code]) {
        return CODE_MESSAGES[code];
    }

    // Prefer whatever the server actually said. Guessing here is what made a
    // simple validation problem look like a broken feature.
    if (error && error.serverMessage) {
        return {
            id: 'gui.login.failedWithReason',
            defaultMessage: `${messages.failed.defaultMessage}（${error.serverMessage}）`
        };
    }

    // An unrecognised status can still be acted on: it points at the terminal.
    if (code === 'AUTH_REQUEST_FAILED' && error && error.status) {
        return {
            id: messages.serverError.id,
            defaultMessage: messages.serverError.defaultMessage,
            values: {status: error.status}
        };
    }

    return messages.failed;
};

export {CODE_MESSAGES, describeAuthError, messages};
export default describeAuthError;
